// packages/web-ui/src/api/clients/skills.ts
//
// Typed client for the `/api/v1/skills` surface. Mirrors the
// `clients/settings.ts` shape — all functions return `Result<T, ApiError>`
// per parent-CLAUDE.md "no throws across boundaries".

import type { Result } from '@tessa-lean/base';
import type {
  SkillCreateRequest,
  SkillDetail,
  SkillMutationResponse,
  SkillsListResponse,
  SkillUpdateRequest,
} from '@tessa-lean/base/api-schemas/index.js';
import { apiCall, type ApiContext, type ApiError } from '../client.js';

/** GET /api/v1/skills — list summaries plus the resolved on-disk root. */
export async function listSkills(
  ctx: ApiContext,
): Promise<Result<SkillsListResponse, ApiError>> {
  return apiCall<undefined, SkillsListResponse>(ctx, 'GET', `/api/v1/skills`);
}

/** GET /api/v1/skills/:name — full detail with body. */
export async function getSkill(
  ctx: ApiContext,
  name: string,
): Promise<Result<SkillDetail, ApiError>> {
  return apiCall<undefined, SkillDetail>(
    ctx,
    'GET',
    `/api/v1/skills/${encodeURIComponent(name)}`,
  );
}

/** POST /api/v1/skills — create a new skill from typed fields. */
export async function createSkill(
  ctx: ApiContext,
  body: SkillCreateRequest,
): Promise<Result<SkillMutationResponse, ApiError>> {
  return apiCall<SkillCreateRequest, SkillMutationResponse>(
    ctx,
    'POST',
    `/api/v1/skills`,
    body,
  );
}

/** PUT /api/v1/skills/:name — replace with full SKILL.md content. */
export async function updateSkill(
  ctx: ApiContext,
  name: string,
  content: string,
): Promise<Result<SkillMutationResponse, ApiError>> {
  const body: SkillUpdateRequest = { content };
  return apiCall<SkillUpdateRequest, SkillMutationResponse>(
    ctx,
    'PUT',
    `/api/v1/skills/${encodeURIComponent(name)}`,
    body,
  );
}

/** DELETE /api/v1/skills/:name — remove the skill folder. */
export async function deleteSkill(
  ctx: ApiContext,
  name: string,
): Promise<Result<SkillMutationResponse, ApiError>> {
  return apiCall<undefined, SkillMutationResponse>(
    ctx,
    'DELETE',
    `/api/v1/skills/${encodeURIComponent(name)}`,
  );
}

export const skillsApi = {
  listSkills,
  getSkill,
  createSkill,
  updateSkill,
  deleteSkill,
};

export type SkillsApi = typeof skillsApi;
